import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SelectorDeIdioma, useIdioma } from '../idioma';
import { useSesion } from '../sesion';
import { supabase } from '../supabase';

/**
 * Contraseña (08-10-2026, fase 2 del plan de usuarios): todo con Supabase Auth, nada a mano contra la base.
 * Mismas reglas que en Supabase (mínimo 10, al menos una letra y un número) para avisar antes de enviar.
 */
export const MINIMO_CONTRASENA = 10;

/** El problema de la contraseña en español (clave de `t()`), o null si sirve. */
export function problemaDeContrasena(clave: string, repetida?: string): string | null {
  if (clave.length < MINIMO_CONTRASENA) return 'Usa al menos 10 caracteres.';
  if (!/[a-zA-Z]/.test(clave) || !/[0-9]/.test(clave)) return 'Mezcla letras y números.';
  if (repetida !== undefined && clave !== repetida) return 'Las dos contraseñas no coinciden.';
  return null;
}

/** Lo que responde Supabase, dicho sin jerga. */
export function errorDeClave(mensaje: string): string {
  if (/invalid login credentials/i.test(mensaje)) return 'Correo o contraseña incorrectos. Si nunca creaste una contraseña, entra con el enlace al correo.';
  if (/email not confirmed/i.test(mensaje)) return 'Primero confirma tu correo: toca el botón del mensaje que te enviamos.';
  if (/pwned|leaked|compromised/i.test(mensaje)) return 'Esa contraseña apareció en filtraciones de otros sitios. Elige otra.';
  if (/weak|password should/i.test(mensaje)) return 'Esa contraseña es muy débil. Usa al menos 10 caracteres con letras y números.';
  if (/same.*password|different from the old/i.test(mensaje)) return 'Elige una contraseña distinta a la anterior.';
  if (/captcha/i.test(mensaje)) return 'No pudimos comprobar que eres una persona. Recarga la página y vuelve a intentarlo.';
  if (/rate limit|too many/i.test(mensaje)) return 'Hiciste muchos intentos seguidos. Espera unos minutos y vuelve a intentarlo.';
  return 'No pudimos hacerlo. Vuelve a intentarlo en un momento.';
}

/** Campo de contraseña con «ver / ocultar». */
export function CampoContrasena({ etiqueta, valor, cambiar, nueva, autoFocus }: { etiqueta: string; valor: string; cambiar: (v: string) => void; nueva?: boolean; autoFocus?: boolean }) {
  const { t } = useIdioma();
  const [ver, setVer] = useState(false);
  return (
    <label className="campo">
      <span>{etiqueta}</span>
      <span className="fila-acciones" style={{ gap: 8, flexWrap: 'nowrap' }}>
        <input type={ver ? 'text' : 'password'} required autoFocus={autoFocus} value={valor} onChange={(e) => cambiar(e.target.value)} autoComplete={nueva ? 'new-password' : 'current-password'} style={{ flex: 1 }} />
        <button type="button" className="btn claro chico" onClick={() => setVer(!ver)}>{ver ? t('Ocultar') : t('Ver')}</button>
      </span>
    </label>
  );
}

/** Formulario de contraseña nueva (dos veces), para la pantalla de recuperar y para Tu perfil. */
function FormularioNueva({ listo, boton }: { listo: () => void; boton: string }) {
  const { t } = useIdioma();
  const [clave, setClave] = useState('');
  const [repetida, setRepetida] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const problema = clave ? problemaDeContrasena(clave, repetida || undefined) : null;

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    const p = problemaDeContrasena(clave, repetida);
    if (p) return setError(t(p));
    setError('');
    setGuardando(true);
    const { error: fallo } = await supabase.auth.updateUser({ password: clave });
    setGuardando(false);
    if (fallo) return setError(t(errorDeClave(fallo.message)));
    setClave('');
    setRepetida('');
    listo();
  }

  return (
    <form onSubmit={guardar} className="formulario">
      <CampoContrasena etiqueta={t('Contraseña nueva')} valor={clave} cambiar={setClave} nueva autoFocus />
      <CampoContrasena etiqueta={t('Escríbela otra vez')} valor={repetida} cambiar={setRepetida} nueva />
      <span className="apunte">{problema ? t(problema) : t('Al menos 10 caracteres, con letras y números.')}</span>
      <button className="btn" disabled={guardando || !clave || !repetida}>{guardando ? t('Guardando…') : boton}</button>
      {error && <p className="error">{error}</p>}
    </form>
  );
}

/**
 * /nueva-contrasena: adonde lleva el correo de «Crea una contraseña nueva». El enlace abre la sesión solo
 * (detectSessionInUrl); si no la abre, el enlace venció o ya se usó.
 */
export function NuevaContrasena() {
  const { t } = useIdioma();
  const ir = useNavigate();
  const { sesion, cargando } = useSesion();
  const [esperando, setEsperando] = useState(true);

  // El enlace tarda un instante en convertirse en sesión: se espera un poco antes de decir que venció.
  useEffect(() => {
    const reloj = setTimeout(() => setEsperando(false), 2500);
    return () => clearTimeout(reloj);
  }, []);

  return (
    <main className="portada">
      <div className="idioma-flotante"><SelectorDeIdioma /></div>
      <div className="portada-caja tarjeta">
        {sesion ? (
          <>
            <h1 className="titulo-portada">{t('Crea tu contraseña nueva')}</h1>
            <p className="apunte">{t('La usarás para entrar con tu correo. También puedes seguir entrando con el enlace o con Google.')}</p>
            <FormularioNueva boton={t('Guardar y entrar')} listo={() => ir('/marcas', { replace: true })} />
          </>
        ) : cargando || esperando ? (
          <p className="apunte">{t('Un momento…')}</p>
        ) : (
          <>
            <h1 className="titulo-portada">{t('Este enlace ya no sirve')}</h1>
            <p className="apunte">{t('Los enlaces para crear contraseña vencen en 1 hora y sirven una sola vez. Pide uno nuevo.')}</p>
            <Link className="btn" to="/entrar?clave=1">{t('Pedir otro enlace')}</Link>
          </>
        )}
      </div>
    </main>
  );
}

/** Tarjeta de Tu perfil: crear o cambiar la contraseña con la sesión abierta. */
export function TarjetaContrasena() {
  const { t } = useIdioma();
  const [abierta, setAbierta] = useState(false);
  const [lista, setLista] = useState(false);
  return (
    <section className="tarjeta col" style={{ gap: 12, padding: 28 }}>
      <b className="red-h3">{t('Tu contraseña')}</b>
      <span className="apunte">{t('Si entras con el enlace al correo o con Google, puedes crear una contraseña para entrar también con ella.')}</span>
      {lista && <span className="apunte"><b>{t('Listo, tu contraseña quedó guardada.')}</b></span>}
      {abierta
        ? <FormularioNueva boton={t('Guardar contraseña')} listo={() => { setAbierta(false); setLista(true); }} />
        : <button className="btn claro" style={{ alignSelf: 'flex-start' }} onClick={() => { setAbierta(true); setLista(false); }}>{t('Crear o cambiar contraseña')}</button>}
    </section>
  );
}
